import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCinematicSlides } from '../hooks/useCinematicSlides';
import AppNav from '../components/common/AppNav';
import { hasSeenSlideshow, markSlideshowSeen } from '../utils/seenState';
import { getPublicBoutiques, getPublicBoutiqueProducts } from '../api/portalApi';
import '../styles/public.css';

const CHINA_ACCENT = '#ef4444';

type GalleryItem = {
  src: string;
  productName: string;
  boutiqueName: string;
  boutiqueId: string;
};

const PROCESS_STEPS = [
  {
    num: '01',
    icon: '🎯',
    title: 'Tell us what you make',
    body: 'Are you running a garment workshop, a grain processing unit, a metalwork shop? Tell us your production setup and your growth target — we start from your business, not from a catalog.',
  },
  {
    num: '02',
    icon: '🔍',
    title: 'We find the right machine',
    body: 'We tap our verified network of Chinese manufacturers — the same factories supplying industrial buyers worldwide — and match your production needs to the right equipment at the right price.',
  },
  {
    num: '03',
    icon: '✅',
    title: 'Quality check before shipment',
    body: 'Our team or a trusted agent inspects the equipment before it leaves China. Specs, compliance, test runs — all confirmed and shared with you before sign-off. No surprises on delivery.',
  },
  {
    num: '04',
    icon: '🚢',
    title: 'Logistics fully handled',
    body: 'Sea freight, customs clearance, import documentation — we manage everything so you never deal with the complexity of international sourcing. Track your shipment live on the NomadMe globe.',
  },
  {
    num: '05',
    icon: '⚙️',
    title: 'Delivered to your floor',
    body: 'Last-mile delivery to your workshop, factory, or farm. New production capacity starts the day your equipment arrives — and your business takes its next step forward.',
  },
];

const FOR_WHO = [
  {
    icon: '🧵',
    sector: 'Garment & Textiles',
    description: 'Cutting machines, sewing stations, finishing equipment. Move from hand-stitching to industrial output and take on orders you couldn\'t before.',
    examples: ['Industrial sewing machines', 'Fabric cutting tables', 'Embroidery stations'],
  },
  {
    icon: '🌾',
    sector: 'Farming & Agriprocessing',
    description: 'Grain mills, irrigation pumps, sorting machines, dryers. Turn your harvest into a scalable processing business that commands better margins.',
    examples: ['Grain mills & threshers', 'Irrigation pumps', 'Produce dryers & sorters'],
  },
  {
    icon: '🔩',
    sector: 'Manufacturing & Metal',
    description: 'Welding sets, lathes, compressors, packaging lines. Upgrade your workshop into a factory and compete for contracts that were out of reach.',
    examples: ['Welding & cutting sets', 'Metal lathes & drills', 'Packaging equipment'],
  },
];

const ChinaPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('landing');
  const seen = hasSeenSlideshow();
  const { current, slideClass, advance, retreat, goTo } = useCinematicSlides(3, 5800, 2, seen ? 2 : 0);
  const [exploreOpen, setExploreOpen] = useState(false);
  const exploreRef = useRef<HTMLDivElement>(null);
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [galleryLoading, setGalleryLoading] = useState(true);

  useEffect(() => {
    if (!seen) markSlideshowSeen();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchGallery = async () => {
      try {
        const { boutiques } = await getPublicBoutiques();
        const chinaShops = boutiques.filter((b) =>
          b.location?.toLowerCase().includes('china')
        );
        if (chinaShops.length === 0) {
          if (!cancelled) setGalleryLoading(false);
          return;
        }
        const results = await Promise.all(
          chinaShops.map((b) =>
            getPublicBoutiqueProducts(b._id).then((r) => ({ boutique: b, products: r.products }))
          )
        );
        const items: GalleryItem[] = [];
        for (const { boutique, products } of results) {
          for (const product of products) {
            if (product.coverImageUrl) {
              items.push({
                src: product.coverImageUrl,
                productName: product.name,
                boutiqueName: boutique.name,
                boutiqueId: boutique._id,
              });
            }
          }
        }
        if (!cancelled) {
          setGalleryItems(items);
          setGalleryLoading(false);
        }
      } catch {
        if (!cancelled) setGalleryLoading(false);
      }
    };
    fetchGallery();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') advance();
      if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   retreat();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [advance, retreat]);

  const handleExplore = () => {
    setExploreOpen(true);
    setTimeout(() => {
      exploreRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  };

  return (
    <div style={exploreOpen ? { height: '100dvh', overflowY: 'auto' } : undefined}>

      {/* ── Cinematic slideshow ───────────────────────────── */}
      <div
        className="cin-shell"
        style={exploreOpen ? { position: 'relative' } : undefined}
      >
        <AppNav accentColor={CHINA_ACCENT} />

        {/* ── Slide 1: Hero ──────────────────────────────── */}
        <div className={slideClass(0)}>
          <div className="cin-bg cin-bg--china">
            <div className="cin-stars" />
            <div className="cin-orb cin-orb--china" />
            <div className="cin-overlay" />
          </div>
          <div className="cin-content" style={{ paddingTop: 60 }}>
            <div className="cin-inner">
              <span className="cin-label cin-label--red">{t('chinaPage.slide1Label')}</span>
              <h1 className="cin-headline">
                {t('chinaPage.slide1Headline')}
              </h1>
              <p className="cin-subline">
                {t('chinaPage.slide1Sub')}
              </p>
              <div className="cin-gap--sm" />
              <button className="cin-action-btn cin-action-btn--red" onClick={advance}>
                {t('chinaPage.slide1Cta')} &rsaquo;
              </button>
            </div>
          </div>
        </div>

        {/* ── Slide 2: Capability ────────────────────────── */}
        <div className={slideClass(1)}>
          <div className="cin-bg cin-bg--china">
            <div className="cin-stars" />
            <div className="cin-overlay cin-overlay--heavy" />
          </div>
          <div className="cin-content" style={{ paddingTop: 60 }}>
            <div className="cin-inner">
              <span className="cin-label cin-label--red">{t('chinaPage.slide2Label')}</span>
              <h1 className="cin-headline">
                {t('chinaPage.slide2Headline')}
              </h1>
              <p className="cin-subline">
                {t('chinaPage.slide2Sub')}
              </p>
              <div className="cin-gap--sm" />
              <div className="cin-stat-row">
                <div className="cin-stat">
                  <div className="cin-stat-num" style={{ color: '#ef4444' }}>100%</div>
                  <div className="cin-stat-label">{t('chinaPage.slide2Stat1Label')}</div>
                </div>
                <div className="cin-stat">
                  <div className="cin-stat-num" style={{ color: '#ef4444' }}>20–30%</div>
                  <div className="cin-stat-label">{t('chinaPage.slide2Stat2Label')}</div>
                </div>
              </div>
              <div className="cin-gap--sm" />
              <button className="cin-action-btn cin-action-btn--ghost" style={{ borderColor: 'rgba(239,68,68,0.4)', color: '#ef4444' }} onClick={advance}>
                {t('chinaPage.slide2Continue')}
              </button>
            </div>
          </div>
        </div>

        {/* ── Slide 3: CTA ───────────────────────────────── */}
        <div className={slideClass(2)}>
          <div className="cin-bg cin-bg--china">
            <div className="cin-stars" />
            <div className="cin-overlay" style={{ background: 'rgba(5,6,13,0.38)' }} />
          </div>
          <div className="cin-content" style={{ paddingTop: 60 }}>
            <div className="cin-inner">
              <span className="cin-label cin-label--red">{t('chinaPage.slide3Label')}</span>
              <h1 className="cin-headline">{t('chinaPage.slide3Headline')}</h1>
              <p className="cin-subline">
                {t('chinaPage.slide3Sub')}
              </p>
              <div className="cin-gap--sm" />
              <div className="cin-action-row" style={{ flexWrap: 'wrap', gap: 10 }}>
                <button
                  className="cin-action-btn cin-action-btn--red"
                  onClick={() => navigate('/globe?region=china')}
                >
                  🗺️ {t('chinaPage.slide3Globe')}
                </button>
                <button
                  className="cin-action-btn cin-action-btn--ghost"
                  style={{ borderColor: 'rgba(239,68,68,0.4)', color: '#ef4444' }}
                  onClick={handleExplore}
                >
                  ⚙️ {t('chinaPage.slide3HowItWorks')}
                </button>
                <Link to="/contact-us" className="cin-action-btn cin-action-btn--ghost">
                  💬 {t('chinaPage.slide3Contact')}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* ── Controls ───────────────────────────────────── */}
        <div className="cin-controls">
          <button
            className={`cin-arrow cin-arrow--left${current === 0 ? ' cin-arrow--hidden' : ''}`}
            onClick={retreat}
            aria-label="Previous"
          >
            ‹
          </button>
          <button
            className={`cin-arrow cin-arrow--right${current === 2 ? ' cin-arrow--hidden' : ''}`}
            onClick={advance}
            aria-label="Next"
          >
            ›
          </button>
          <div className="cin-dots">
            {[0, 1, 2].map((i) => (
              <button
                key={i}
                className={`cin-dot${current === i ? ' cin-dot--active' : ''}`}
                onClick={() => goTo(i)}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── Explore expansion ────────────────────────────── */}
      {exploreOpen && (
        <div ref={exploreRef} className="china-explore">

          {/* Who it's for */}
          <section className="china-explore-section">
            <p className="china-explore-eyebrow">{t('chinaPage.exploreForWhoEyebrow')}</p>
            <h2 className="china-explore-title">{t('chinaPage.exploreForWhoTitle')}</h2>
            <p className="china-explore-sub">
              {t('chinaPage.exploreForWhoSub')}
            </p>
            <div className="china-forwho-grid">
              {FOR_WHO.map((w) => (
                <div key={w.sector} className="china-forwho-card">
                  <span className="china-forwho-icon">{w.icon}</span>
                  <h3 className="china-forwho-sector">{w.sector}</h3>
                  <p className="china-forwho-desc">{w.description}</p>
                  <ul className="china-forwho-list">
                    {w.examples.map((ex) => (
                      <li key={ex}>{ex}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {/* Process steps */}
          <section className="china-explore-section china-explore-section--darker">
            <p className="china-explore-eyebrow">{t('chinaPage.exploreProcessEyebrow')}</p>
            <h2 className="china-explore-title">{t('chinaPage.exploreProcessTitle')}</h2>
            <p className="china-explore-sub">
              {t('chinaPage.exploreProcessSub')}
            </p>
            <div className="china-step-grid">
              {PROCESS_STEPS.map((step) => (
                <div key={step.num} className="china-step-card">
                  <div className="china-step-header">
                    <span className="china-step-num">{step.num}</span>
                    <span className="china-step-icon">{step.icon}</span>
                  </div>
                  <h3 className="china-step-title">{step.title}</h3>
                  <p className="china-step-body">{step.body}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Gallery — dynamic from China-located shops */}
          {(galleryLoading || galleryItems.length > 0) && (
            <section className="china-explore-section">
              <p className="china-explore-eyebrow">{t('chinaPage.exploreGalleryEyebrow')}</p>
              <h2 className="china-explore-title">{t('chinaPage.exploreGalleryTitle')}</h2>
              <p className="china-explore-sub">
                {t('chinaPage.exploreGallerySub')}
              </p>
              {galleryLoading ? (
                <div className="china-gallery">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="china-gallery-item china-gallery-item--skeleton">
                      <div className="china-gallery-skeleton-img" />
                      <div className="china-gallery-skeleton-label" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="china-gallery">
                  {galleryItems.map((item, i) => (
                    <Link
                      key={`${item.boutiqueId}-${i}`}
                      to={`/shop/boutique/${item.boutiqueId}`}
                      className="china-gallery-item china-gallery-item--linked"
                    >
                      <div className="china-gallery-img-wrap">
                        <img
                          src={item.src}
                          alt={item.productName}
                          loading="lazy"
                          className="china-gallery-img"
                        />
                        <div className="china-gallery-overlay">
                          <span className="china-gallery-cta">View shop →</span>
                        </div>
                      </div>
                      <div className="china-gallery-meta">
                        <span className="china-gallery-product-name">{item.productName}</span>
                        <span className="china-gallery-shop-badge">{item.boutiqueName}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* CTA */}
          <section className="china-explore-cta">
            <h2 className="china-explore-cta-title">{t('chinaPage.exploreCtaTitle')}</h2>
            <p className="china-explore-cta-sub">
              {t('chinaPage.exploreCtaSub')}
            </p>
            <div className="china-explore-cta-btns">
              <Link to="/contact-us" className="cin-action-btn cin-action-btn--red" style={{ textDecoration: 'none' }}>
                💬 {t('chinaPage.exploreCtaContact')}
              </Link>
              <button
                className="cin-action-btn cin-action-btn--ghost"
                onClick={() => navigate('/globe?region=china')}
              >
                🗺️ {t('chinaPage.exploreCtaGlobe')}
              </button>
            </div>
          </section>

        </div>
      )}
    </div>
  );
};

export default ChinaPage;
